import React from "react";
import axios from "axios";
import lodash from "lodash";
import { observable, action, computed, toJS } from "mobx";

const userMessage = user => {
  let optOutMessage = [];
  if (!user.twilio_sms) {
    optOutMessage.push("Opted out of text messages via service provider");
  }
  if (!user.group_message_email) {
    optOutMessage.push("Opted out of group message emails");
  }
  if (!user.group_message_sms) {
    optOutMessage.push("Opted out of group message texts");
  }
  optOutMessage = optOutMessage.join(" and ");
  if (optOutMessage.length > 0) {
    optOutMessage = (
      <span key={`helper-${user.id}`} style={{ color: "#E23E74" }}>
        ({optOutMessage})
      </span>
    );
  }

  return optOutMessage;
};

const sortUsers = (a, b) => {
  const aName = [a.last_name, a.first_name].join(" ").toLowerCase();
  const bName = [b.last_name, a.first_name].join(" ").toLowerCase();
  if (aName < bName) return -1;
  if (aName > bName) return 1;
  return 0;
};

class MessagingStorage {
  @observable __loading = false;
  @observable __search = "";
  @observable __messages = [];
  @observable __optionsGroups = [];
  @observable __stop = false;
  @observable __selectedUser = [];
  @observable __subject = "";
  @observable __message = "";
  @observable __files = [];
  @observable __sending = false;

  // For new message
  @observable __messageType = "E-Mail";
  @observable __includeInactive = false;

  __locations = [];
  __users = [];
  __positions = [];
  __groups = [];
  __userLocations = [];
  __userPositions = [];
  __maxAttachmentBytes = null;
  __page = 0;

  constructor(locations, maxAttachmentBytes) {
    this.__locations = locations;
    this.__maxAttachmentBytes = maxAttachmentBytes;

    Promise.all([
      this.loadMessages(),
      this.loadUsers(),
      this.loadPositions(),
      this.loadGroups()
    ]).then(result => {
      this.__users = result[1].data.sort(sortUsers);
      this.__positions = result[2].data;
      this.__groups = {};
      this.loadAddition();
      this.__loading = false;
    });

    this.hanlderSearch = lodash.debounce(this.find, 400);
  }

  @computed
  get loading() {
    return this.__loading;
  }

  @computed
  get stop() {
    return this.__stop;
  }

  @computed
  get messages() {
    return toJS(this.__messages);
  }

  @computed
  get search() {
    return this.__search;
  }

  @computed
  get messageType() {
    return this.__messageType;
  }

  @computed
  get includeInactive() {
    return this.__includeInactive;
  }

  @computed
  get groups() {
    return toJS(this.__optionsGroups);
  }

  @computed
  get selectedUser() {
    return toJS(this.__selectedUser);
  }

  @computed
  get subject() {
    return this.__subject;
  }

  @computed
  get message() {
    return this.__message;
  }

  @computed
  get invalid() {
    return (
      this.__message.length === 0 ||
      this.__selectedUser.length === 0 ||
      this.attachmentsTooLarge
    );
  }

  @computed
  get files() {
    return toJS(this.__files);
  }

  @computed
  get attachmentsTooLarge() {
    const totalBytes = lodash.sumBy(this.__files, "size");
    return totalBytes > this.__maxAttachmentBytes;
  }

  @computed
  get sending() {
    return this.__sending;
  }

  loadUsers = () =>
    axios.get(
      `/users.json?${
        this.__includeInactive ? "archived=0" : "active=1&archived=0"
      }`
    );
  loadPositions = () => axios.get("/positions.json?archived=false");
  loadGroups = () => axios.get(`/groups.json?only_user_ids=true`);
  loadUserLocations = ids =>
    axios.get(`/user_locations.json?location_id=${ids}`);
  loadUserPositions = ids =>
    axios.get(`/employee_positions_data.json?position_id=${ids}`);

  loadAddition = () => {
    const locationIds = this.__locations.map(item => item.id).join(",");
    const positionIds = this.__positions.map(item => item.id).join(",");

    Promise.all([
      this.loadUserLocations(locationIds),
      this.loadUserPositions(positionIds)
    ]).then(result => {
      this.__userLocations = result[0].data;
      this.__userPositions = result[1].data;
      this.buildEmployers();
    });
  };

  @action
  addFiles = files => {
    lodash.forEach(files, file => {
      this.__files.push(file);
    });
  };

  @action
  deleteFile = (fileId, index) => this.__files.splice(index, 1);

  @action
  buildEmployers = () => {
    const userIds = this.__users.map(user => user.id);
    const tmp = [
      {
        label: `All Employees (${this.__users.length} Employees)`,
        value: userIds.join(",")
      }
    ];

    // Add users
    lodash.forEach(this.__users, (user, index) => {
      const name = [user.last_name, user.first_name].join(", ");

      if (user.id) {
        tmp.push({
          label: [name, <span key={`msg-${index}`}> {userMessage(user)}</span>],
          value: user.id.toString()
        });
      }
    });

    // Add grouping positions
    lodash.forEach(this.__positions, position => {
      const filteredEmployeePositions = lodash.filter(this.__userPositions, {
        position_id: position.id
      });
      const userPositionsIds = lodash.intersection(
        lodash.map(filteredEmployeePositions, "user_id"),
        userIds
      );

      if (userPositionsIds.length > 0) {
        tmp.push({
          label: `All ${position.name} positions (${
            userPositionsIds.length
          } Employees)`,
          value: userPositionsIds.join(",")
        });
      }
    });

    // Add grouping with locations and positions
    lodash.forEach(this.__locations, location => {
      const filteredPrimaryUserLocations = lodash.filter(this.__userLocations, {
        location_id: location.id,
        primary: true
      });
      const filteredNonPrimaryUserLocations = lodash.filter(
        this.__userLocations,
        { location_id: location.id, primary: false }
      );
      const primaryUserLocationIds = lodash.intersection(
        lodash.map(filteredPrimaryUserLocations, "user_id"),
        userIds
      );
      const nonPrimaryUserLocationIds = lodash.intersection(
        lodash.map(filteredNonPrimaryUserLocations, "user_id"),
        userIds
      );

      if (primaryUserLocationIds.length > 0) {
        tmp.push({
          label: `All Employees at ${location.name}  (Primary)
        - ${primaryUserLocationIds.length} Employees`,
          value: primaryUserLocationIds.join(",")
        });
      }

      if (nonPrimaryUserLocationIds.length > 0) {
        tmp.push({
          label: `All Employees at at ${location.name} (Non-Primary)
        - ${nonPrimaryUserLocationIds.length} Employees`,
          value: nonPrimaryUserLocationIds.join(",")
        });
      }

      lodash.forEach(this.__positions, position => {
        const filteredEmployeePositions = lodash.filter(this.__userPositions, {
          position_id: position.id
        });
        const userPositionsIds = lodash.intersection(
          lodash.map(filteredEmployeePositions, "user_id"),
          userIds
        );
        const userIdsByPositionAndPrimaryLocation = lodash.intersection(
          primaryUserLocationIds,
          userPositionsIds
        );
        const userIdsByPositionAndNonPrimaryLocation = lodash.intersection(
          nonPrimaryUserLocationIds,
          userPositionsIds
        );

        if (userIdsByPositionAndPrimaryLocation.length > 0) {
          tmp.push({
            label: `All ${position.name} positions at ${location.name} (Primary)
          - ${userIdsByPositionAndPrimaryLocation.length} Employees`,
            value: userIdsByPositionAndPrimaryLocation.join(",")
          });
        }

        if (userIdsByPositionAndNonPrimaryLocation.length > 0) {
          tmp.push({
            label: `All ${position.name} positions at ${
              location.name
            } (Non-Primary)
          - ${userIdsByPositionAndNonPrimaryLocation.length} Employees`,
            value: userIdsByPositionAndNonPrimaryLocation.join(",")
          });
        }
      });
    });

    // Add grouping with groups
    lodash.forEach(this.__groups, group => {
      const end = group.users.length === 1 ? "" : "s";
      const groupUserIds = lodash.intersection(
        lodash.map(group.users, "id"),
        userIds
        );
      const label = `All Employees at ${group.name} locations - ${
        groupUserIds.length
      } employee${end}`;
      if (groupUserIds.length > 0) {
        tmp.push({ label: label, value: groupUserIds.join(",") });
      }
    });

    // Add grouping with groups and positions
    lodash.forEach(this.__groups, group => {
      const groupUserIds = lodash.intersection(
        lodash.map(group.users, "id"),
        userIds
      );

      lodash.forEach(this.__positions, position => {
        const filteredEmployeePositions = lodash.filter(this.__userPositions, {
          position_id: position.id
        });
        const userPositionsIds = lodash.intersection(
          lodash.map(filteredEmployeePositions, "user_id"),
          groupUserIds
        );

        if (userPositionsIds.length > 0) {
          const end = userPositionsIds.length === 1 ? "" : "s";
          const label = `All ${position.name} at Group ${group.name} - ${
            userPositionsIds.length
          } employee${end}`;
          if (userPositionsIds.length > 0) {
            tmp.push({ label: label, value: userPositionsIds.join(",") });
          }
        }
      });
    });

    this.__optionsGroups = tmp;
  };

  @action
  find() {
    this.__page = 0;
    this.__stop = false;
    this.__messages.clear();

    this.loadMessages();
  }

  @action
  loadMessages = () => {
    if (this.__loading || this.__stop) {
      return;
    }

    this.__page += 1;
    this.__loading = true;

    const params = ["page_size=25"];
    if (this.__search) {
      params.push(`search=${this.__search}`);
    }
    if (this.__page > 0) {
      params.push(`page=${this.__page}`);
    }

    return axios
      .get(`/messaging.json?${params.join("&")}`)
      .then(this.buildMessages);
  };

  @action
  destroyMessage = e => {
    let messageId = e.target.id ;
    axios.delete(`/message_logs/${messageId}.json`).then(() => {
      this.__messages = this.__messages.filter(message => message.id != messageId);
      toastr.success("The message has been deleted.");
    });
  };

  @action
  updateSearch = text => {
    this.__search = text;
    this.hanlderSearch();
  };

  @action
  buildMessages = resp => {
    if (!resp) {
      return;
    }
    if (resp.data.length < 25) {
      this.__stop = true;
    }

    this.__messages = this.__messages.concat(resp.data);
    this.__loading = false;
  };

  @action
  toggleMessageType = type => {
    this.__messageType = type;
    this.__message = "";
  };

  @action
  toggleIncludeInactive = () => {
    this.__includeInactive = !this.__includeInactive;

    this.loadUsers().then(resp => {
      this.__users = resp.data.sort(sortUsers);
      this.buildEmployers();
    });
  };

  @action
  changeSelectUsers = data => {
    this.__selectedUser = data;
  };

  @action
  changeInput = e => {
    const name = e.target.name;
    this[name] = e.target.value;
  };

  @action
  submit = callback => {
    if (this.__sending || this.invalid) {
      return;
    }
    this.__sending = true;

    const ids = lodash.map(this.selectedUser, "value").join(",");
    const data = new FormData();

    data.append("email_message", this.__messageType);
    data.append("subject", this.subject);
    data.append("message", this.message);
    data.append("data", ids);

    if (this.__messageType === "E-Mail") {
      this.files.forEach(file => {
        if (file instanceof File){
          data.append("attachments[]", file, file.name);
        }
      });
    }

    axios
      .post("/group_message.json", data)
      .then(() => {
        toastr.success("Sending your message... History will appear shortly.");
        this.__sending = false;
        this.__subject = "";
        this.__message = "";
        this.__messageType = "E-Mail";
        this.__selectedUser.clear();
        this.__files.clear();
        callback();
      })
      .catch(error => {
        this.__sending = false;
        const message = lodash.get(
          error,
          "response.data.error",
          "Something went wrong. Please try again."
        );
        toastr.error(message);
      });
  };
}

export default MessagingStorage;
